/** @prettier */
import { DraftQuestion } from "./questionApi";

// slightly unsafe signature of Object.keys,
const keys = Object.keys as <T>(object: T) => Array<keyof T>;

// All the same shape, currently.
type Subject = { uuid: string };
type Category = Subject;
type Subcategory = Subject;

export function validateQuestion(
    question: DraftQuestion,
    subjectsList: Subject[] | undefined,
    categoriesList: Category[] | undefined,
    subcategoriesList: Subcategory[] | undefined,
) {
    const {
        subject,
        category,
        subcategory,
        learningObjective,
        stem,
        answerChoices,
        correctAnswerExplanation,
        incorrectAnswerExplanation,
        references,
        keyConcept,
        reinforcementQuestions,
    } = question;

    let validationErrors = {
        subject: "",
        category: "",
        subcategory: "",
        learningObject: "",
        questionStem: "",
        answerChoices: "",
        keyConcept: "",
        references: "",
        correctAnswerExplanation: "",
        incorrectAnswerExplanation: "",
        reinforcementQuestions: "",
    };

    if (subject) {
        if (
            subjectsList &&
            !subjectsList.find((s) => s.uuid === subject.uuid)
        ) {
            validationErrors.subject = subject.name + " is not a valid Subject";
        }
    } else {
        validationErrors.subject = "Subject is required.";
    }

    if (category) {
        if (
            categoriesList &&
            !categoriesList.find((c) => c.uuid === category.uuid)
        ) {
            validationErrors.category =
                category.name + "is not a valid Category for " + subject?.name;
        }
    } else {
        validationErrors.category = "Category is required.";
    }

    if (subcategory) {
        if (
            subcategoriesList &&
            !subcategoriesList.find((s) => s.uuid === subcategory.uuid)
        ) {
            validationErrors.subcategory =
                subcategory.name +
                " is not a valid Subcategory for " +
                category?.name;
        }
    } else {
        validationErrors.subcategory = "Subcategory is required.";
    }

    // Validate question stem
    if (!stem) {
        validationErrors.questionStem = "Question stem cannot be empty.";
    }

    // Validate learning objective
    if (!learningObjective) {
        validationErrors.learningObject = "Learning objective cannot be empty.";
    }

    // Validate answer choices
    if (answerChoices.length) {
        const hasEmptyAnswerChoice = answerChoices.find(function (
            answerChoice,
        ) {
            return !answerChoice.choice;
        });
        const uniqueChoices = [
            ...new Set(
                answerChoices.map((ac) => ac.choice && ac.choice.toLowerCase()),
            ),
        ];
        if (hasEmptyAnswerChoice) {
            validationErrors.answerChoices = "Answer options cannot be empty.";
        } else if (!answerChoices.length || answerChoices.length < 5) {
            validationErrors.answerChoices =
                "At least five answer options are required.";
        } else if (uniqueChoices.length !== answerChoices.length) {
            validationErrors.answerChoices =
                "Answers options cannot have duplicates.";
        } else {
            const correctItem = answerChoices.find(function (item) {
                return !!item.correct;
            });
            if (!correctItem) {
                validationErrors.answerChoices =
                    "The correct answer must be selected.";
            }
        }
    } else {
        validationErrors.answerChoices = "Answer options are required.";
    }

    // Validate reinforcement questions
    if (reinforcementQuestions) {
        // NOTE: the length validations are important for save, otherwise the request may fail
        const hasTooLongReinforcementQuestion = reinforcementQuestions.some(
            (rq) => rq.question.length > 250,
        );
        if (hasTooLongReinforcementQuestion) {
            validationErrors.reinforcementQuestions =
                "Reinforcement questions cannot be over 250 characters.";
        }
        const hasTooLongReinforcementAnswer = reinforcementQuestions.some(
            (rq) => rq.answer.length > 250,
        );
        if (hasTooLongReinforcementAnswer) {
            validationErrors.reinforcementQuestions =
                "Reinforcement question answers cannot be over 250 characters.";
        }
        const hasEmptyReinforcementQuestion = reinforcementQuestions.some(
            (rq) => !rq.question.length,
        );
        if (hasEmptyReinforcementQuestion) {
            validationErrors.reinforcementQuestions =
                "Reinforcement questions cannot be empty.";
        }
        const hasEmptyReinforcementQuestionAnswer = reinforcementQuestions.some(
            (rq) => !rq.answer.length,
        );
        if (hasEmptyReinforcementQuestionAnswer) {
            validationErrors.reinforcementQuestions =
                "Reinforcement question answers cannot be empty.";
        }
    }

    // Validate key concept
    if (!keyConcept.trim()) {
        validationErrors.keyConcept = "Key concept is required.";
    }

    // Validate references
    if (references) {
        const hasEmptyReference = references.some(
            (reference) => !reference.length,
        );
        if (hasEmptyReference) {
            validationErrors.references = "References cannot be empty.";
        }
    } else {
        validationErrors.references = "References are required.";
    }

    // Validate explanation
    if (!correctAnswerExplanation.trim()) {
        validationErrors.correctAnswerExplanation =
            "Correct answer explanation is required.";
    }
    if (!incorrectAnswerExplanation.trim()) {
        validationErrors.incorrectAnswerExplanation =
            "Incorrect answers explanation is required.";
    }
    return Object.assign(validationErrors, {
        count: keys(validationErrors).filter(
            (key) => validationErrors?.[key].length > 0,
        ).length,
    });
}
